import { AsyncState, HttpResult, loadedAsyncState, loadingAsyncState, Option, Updater } from 'widgets-for-react'
import { initialNotFoundState, NotFoundState } from '../notFound/notFoundState'
import { BlocksState } from './blocks/blocksState'
import { MembershipCaclulatorState } from '../membershipCalculator/membershipCaclulatorState'
import { VideoProviderDetails } from '../../utils/video'
import { SpotlerCMSEvent } from '../../utils/sqzl'

export type CmsPageState = {
  pageWithBlocksState: { kind: "redirect", to: string } | PageWithBlocksState
  notFoundState: NotFoundState
}

export type PageWithBlocksState = AsyncState<PageWithBlocks>

export type PageWithBlocks = Page & {
  blocks: BlocksState
}

export type Page =
  | HomePage
  | GenericPage
  | KnowledgeArticlePage
  | KnowledgeOverviewPage
  | DossierPage
  | CourseGroupPage
  | CourseCategoryPage
  | CoursePage
  | EventPage
  | ArticlePage
  | ActualityPage
  | CasePage
  // [Custom content types]: Add page type to union

export interface HomePage extends BasePage {
  kind: 'home'
}

export interface BasePage {
  image?: string
  title: string
  metaTitle: string
  intro?: string
  video?: VideoProviderDetails
  primaryButton?: Link
  secondaryButton?: Link
  slug: string
  path: string
}

export interface GenericPage extends BasePage {
  kind: 'generic'
  parent: Option<GenericPage>
  menuOpened: boolean
  membershipCalculatorState?: MembershipCaclulatorState
}


export interface KnowledgeArticlePage extends BasePage {
  kind: 'knowledge-article'
  primaryDossier: Option<DossierPage>
  secondaryDossiers: Option<DossierPage[]>
  contactPerson: Option<ContactPerson>
}

export interface CasePage extends BasePage {
  kind: 'case-detail'
  contactPerson: Option<ContactPerson>
}

export interface EventPage extends BasePage {
  kind: 'event'
  eventBoostLabel: string | null
  eventLocation: string
  eventDate: Date
  eventTime: string
  eventMemberPrice: number,
  eventNonMemberPrice: number
  eventFormId: string | null
  Bijeenkomst: { RemoteId: string } | null
}
export interface ArticlePage extends BasePage {
  kind: 'article'
  articleDate: Date
  articleRelevance?: 'Featured' | 'SemiFeatured',
  magazine: 'Logisticx' | 'Globe'
  articleEditor: string,
  photographer: string
}

export interface KnowledgeOverviewPage extends BasePage {
  kind: 'knowledge-overview'
}

export interface DossierPage extends BasePage {
  kind: 'dossier'
  primaryDossier: Option<DossierPage>
  contactPerson: Option<ContactPerson>
  sharePointId: number
}

export interface CourseCategoryPage extends BasePage {
  kind: 'course-category'
  sharePointId: number
}

export interface CourseGroupPage extends BasePage {
  kind: 'course-group'
  primaryCategory: Option<CourseCategoryPage>
  secondaryCategories?: CourseCategoryPage[]
  sharePointId: number
}
export interface CoursePage extends BasePage {
  kind: 'course-details'
  courseInCompany: boolean | null
  courseDurationOverride: string
  courseHideAvailableSeats: boolean | null
  courseHideSchedule: boolean | null
  courseStartDateAndLocationsIntroduction: string | null
  group: Option<CourseGroupPage>
  Evenement: { RemoteId: string } | null
}

export interface ContactPerson {
  kind: 'contact-person'
  Name: string | null
  Title: string | null
  Intro: string | null
  ImageUrl: {
    Title: string
    Url: string
  } | null
  PhoneNumber: string | null
  PhoneText: string | null
  PrimaryButtonTitle: string | null
  PrimaryButtonUrl: {
    Title: string
    Url: string
  } | null
  SecondaryButtonTitle: string | null
  SecondaryButtonUrl: {
    Title: string
    Url: string
  } | null
}

export interface Tag {
  title: string
}

export interface ActualityPage extends BasePage {
  kind: 'actuality-details'
  tags: Tag[] | null
  sharePointId: number
  primaryDossier: Option<DossierPage>
  secondaryDossiers: Option<DossierPage[]>
}

// [Custom content types]: Add a model here

export type Link = {
  slug: string
  title: string
}

type PhoneNumbers = {
  description: string
  phoneNumber: string
}[]

export const initialCmsPageState = (
  pageDataApi: () => Promise<HttpResult<PageWithBlocks>>
): CmsPageState => ({
  pageWithBlocksState: loadingAsyncState(pageDataApi),
  notFoundState: initialNotFoundState
})

export const cmsPageStateUpdaters = {
  pageWithBlocksUpdater: (
    updater: Updater<PageWithBlocksState>
  ) => (state: CmsPageState): CmsPageState => {
    if (state.pageWithBlocksState.kind === 'redirect') {
      return state
    }

    const newState = updater(state.pageWithBlocksState)
    if (state.pageWithBlocksState.kind != 'loaded' && newState.kind === 'loaded') {
      document.title = newState.value.metaTitle ?
        newState.value.metaTitle.replace("[node:title]", newState.value.title).replace("[site:name]", "evofenedex") : newState.value.title
      SpotlerCMSEvent(newState.value)
    }

    return {
      ...state,
      pageWithBlocksState: newState
    }
  },
  notFoundStateUpdater:
    (updater: Updater<NotFoundState>) =>
    (state: CmsPageState): CmsPageState => ({
      ...state,
      notFoundState: updater(state.notFoundState),
    }),
  blocksUpdater:
    (updater: Updater<BlocksState>) =>
    (state: PageWithBlocksState): PageWithBlocksState =>
      state.kind == 'loaded'
        ? loadedAsyncState({
            ...state.value,
            blocks: updater(state.value.blocks),
          })
        : state,
  menuOpenedUpdater: (
    state: PageWithBlocksState
  ): PageWithBlocksState =>
    state.kind == 'loaded' && state.value.kind === 'generic'
      ? loadedAsyncState<PageWithBlocks>({
          ...state.value,
          menuOpened: !state.value.menuOpened,
        })
      : state,
}

